import { Button, Tooltip, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import JSZip from 'jszip';
import * as _ from 'lodash';
import React, { useMemo } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { InvoiceReportGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { fileExtensionToMimeTypeMap } from 'packages/common-frontend/utils';
import { useGetReportsS3DownloadUrlsLazyQueryGQLType } from 'packages/reporting/requests/reporting.gql.generated';

// returns the correct file extension for a Blob which is required for files opening correctly when unzipping
const getFileExtensionFromBlob = (blob: Blob): string | undefined => {
  return Object.entries(fileExtensionToMimeTypeMap).find(
    ([_extension, mimeType]) => mimeType === blob.type,
  )?.[0];
};

type DownloadAllLiveDisbursementNoticesButtonBodyParams = {
  disbursementNoticeReports: InvoiceReportGQLType[];
};
const DownloadAllLiveDisbursementNoticesButtonBody = ({
  disbursementNoticeReports,
}: DownloadAllLiveDisbursementNoticesButtonBodyParams) => {
  const toast = useToast();

  const todayDateStr = commonDate.currentDateStr();

  const [
    getReportsUrlsQuery,
    { loading: isGetReportsUrlsLoading },
  ] = useGetReportsS3DownloadUrlsLazyQueryGQLType({
    fetchPolicy: 'network-only',
    variables: {
      reportIdsWithFilename: disbursementNoticeReports.map((report) => ({
        reportId: report.id,
        filename: `disbursement-notice-${todayDateStr}-${
          report.capitalProviderName
            ? `-${_.kebabCase(report.capitalProviderName)}`
            : ''
        }`,
      })),
      reportConfigType: 'disbursementNotice',
    },
    onCompleted: async ({ getReportsS3DownloadUrls }) => {
      if (!getReportsS3DownloadUrls) {
        toast({
          ...errorToastConfig,
          title: `Failed to retrieve valid download URLs for disbursement notices`,
        });
        return;
      }

      try {
        const zip = new JSZip();

        await Promise.all(
          getReportsS3DownloadUrls.map(async (downloadUrl, index) => {
            const response = await axios({
              url: downloadUrl.s3DownloadUrl,
              method: 'GET',
              responseType: 'blob',
            });

            const fileBlob = new Blob([response.data], {
              type: response.data.type,
            });
            const singleFileName = downloadUrl.filename;
            const singleFileExtension = getFileExtensionFromBlob(fileBlob);

            const fileNameToZip = singleFileExtension
              ? `${index + 1}-${singleFileName}.${singleFileExtension}`
              : `${index + 1}-${singleFileName}`;

            zip.file(fileNameToZip, fileBlob);
          }),
        );

        const zippedFileBlob = await zip.generateAsync({ type: 'blob' });
        const zippedFileName = `disbursement-notices-${todayDateStr}`;

        FileSaver.saveAs(zippedFileBlob, zippedFileName);
      } catch (err) {
        Sentry.captureException(
          `Error when zipping and downloading disbursement notices: ${err}`,
        );

        toast({
          ...errorToastConfig,
          title: `Failed to download disbursement notices`,
        });
      }
    },
  });

  return (
    <Tooltip label="Download all disbursement notices for loan">
      <Button
        variant="unstyled"
        onClick={() => getReportsUrlsQuery()}
        isLoading={isGetReportsUrlsLoading}
        fontWeight={400}
      >
        Download All Disbursement Notices
      </Button>
    </Tooltip>
  );
};

type DownloadAllLiveDisbursementNoticesButtonParams = {
  invoiceReports?: InvoiceReportGQLType[];
};
export const DownloadAllLiveDisbursementNoticesButton = ({
  invoiceReports,
}: DownloadAllLiveDisbursementNoticesButtonParams) => {
  const compactedInvoiceReports = useMemo(() => {
    return invoiceReports?.filter((report) => !!report);
  }, [invoiceReports]);

  const disbursementNoticeReports = compactedInvoiceReports?.filter(
    (report) => report.reportType === 'disbursementNotice',
  );

  if (!disbursementNoticeReports?.length) {
    return (
      <Tooltip label="Disbursement notices are not found">
        <Button isDisabled variant="v2-ghost" fontWeight={400}>
          Download All Disbursement Notices
        </Button>
      </Tooltip>
    );
  }

  return (
    <DownloadAllLiveDisbursementNoticesButtonBody
      disbursementNoticeReports={disbursementNoticeReports}
    />
  );
};
